<template>
  <div class="max-w-6xl mx-auto px-4 sm:px-6">
    <div class="pt-32 pb-10 md:pt-40">
      <div class="text-center">
        <div
          class="inline-flex font-medium bg-clip-text text-transparent bg-[#1ba7ce] dark:bg-gradient-to-r from-green-500 to-green-200 pb-3"
        >
          Last Updated September 15, 2026
        </div>
        <h1
          class="h1 bg-clip-text text-transparent bg-gradient-to-r from-slate-950/60 via-slate-800 to-slate-950/60 dark:from-slate-200/60 dark:via-slate-200 dark:to-slate-200/60 pb-6"
        >
          Privacy Policy
        </h1>
      </div>
      <div class="mt-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-4 py-3 text-sm text-black dark:text-slate-50">
        This is a previous version of the Privacy Policy. It applies until November 16, 2026. After that date the version at
        <a href="/privacy-policy" class="text-[#1ba7ce] underline">workspace369.com/privacy-policy</a> applies.
      </div>
      <div class="mt-20 text-black dark:text-slate-50">
        <h2 class="font-bold text-xl my-10">1. Introduction</h2>
        <p class="my-5">
          Workspace369 LLC ("Workspace369", "we", "us", or "our") provides a
          client-work operating system: customer relationship management,
          projects, proposals, invoicing, payments, scheduling, a shared inbox
          with email, SMS, voice and voicemail, AI-assisted summaries and
          automations, client portals, files, expenses, accounting, and
          reporting (collectively, the "Service"). This Privacy Policy explains
          what personal data we collect when you use the Service, our website at
          workspace369.com, and our mobile and desktop applications, how we use
          it, when we share it, and the choices and rights you have.
        </p>
        <p class="my-5">
          The Service is offered worldwide. Where data protection laws of the
          European Economic Area, the United Kingdom, or Switzerland apply,
          Workspace369 LLC is the data controller for personal data described in
          this Policy, except for Workspace Content (defined below), for which
          our customer is the controller and we act as a processor.
        </p>
        <p class="my-5">
          Contact: Workspace369 LLC, 8225 Emerald Winds Circle, Boynton Beach,
          FL 33473, USA. Privacy requests:
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >.
        </p>

        <h2 class="font-bold text-xl my-10">2. Information we collect</h2>
        <h3 class="font-semibold text-lg my-4">2.1 Account and profile data</h3>
        <p class="my-5">
          When you create an account we collect your name, email address,
          password or authentication credentials, company name, role, profile
          photo, locale, time zone, and billing contact details. If you sign in
          with a third-party identity provider (such as Google), we receive the
          profile information that provider shares with us (name, email, profile
          image).
        </p>
        <h3 class="font-semibold text-lg my-4">2.2 Workspace Content</h3>
        <p class="my-5">
          "Workspace Content" is the data you and your team create or import in
          the Service: client and company records, contacts, notes, files,
          projects, tasks, proposals, invoices, expenses, schedules, automations,
          and the content of your communications handled through the Service —
          emails you connect or forward, SMS and MMS messages, voice calls,
          voicemails and their transcriptions, call recordings, and AI-generated
          summaries of those communications. You control this content; we
          process it on your instructions to provide the Service.
        </p>
        <h3 class="font-semibold text-lg my-4">2.3 Payment and transaction data</h3>
        <p class="my-5">
          Payments for Workspace369 subscriptions are processed by Stripe, Inc.
          ("Stripe"). We do not store full payment card numbers; we receive and
          store limited details from Stripe such as card brand, last four
          digits, expiry, billing address, and transaction identifiers. If you
          connect a Stripe account to accept payments from your own clients
          (Stripe Connect), we receive from Stripe your connected account
          identifier, account status, payout details at a summary level, and
          transaction records for payments processed through the Service,
          including the application fees we charge.
        </p>
        <h3 class="font-semibold text-lg my-4">2.4 Usage, device, and log data</h3>
        <p class="my-5">
          We collect IP addresses, browser and device type, operating system,
          pages and features used, actions taken, timestamps, referring URLs,
          crash and performance logs, and approximate location derived from IP
          address. We use cookies and similar technologies for authentication,
          preferences, analytics, and product improvement. Our website (not the
          authenticated application) uses analytics services including Google
          Analytics and Microsoft Clarity, and advertising measurement tools
          from Google (Google Ads) and Meta (Meta Pixel and Conversions API).
        </p>
        <h3 class="font-semibold text-lg my-4">2.5 Integration data</h3>
        <p class="my-5">
          When you connect third-party services, we receive the data needed to
          operate the integration: for Google Calendar, your calendar list,
          events, and free/busy information; for Google Meet, meeting links we
          create on your instruction; for Gmail (when enabled), message
          metadata and content needed to sync email into your shared inbox; and
          for Stripe Connect, the account data described above. We access
          integration data only with the permissions (scopes) you approve and
          only for the features you use.
        </p>

        <h2 class="font-bold text-xl my-10">3. How we use information</h2>
        <ul class="list-disc pl-6 space-y-3 my-5">
          <li>Provide, operate, secure, and maintain the Service, including syncing your integrations and delivering your communications;</li>
          <li>Process transactions, subscriptions, application fees, invoices, and payouts-related records;</li>
          <li>Provide AI-assisted features you enable, such as communication summaries, drafted content, and workflow suggestions;</li>
          <li>Personalize the Service, remember preferences, and provide customer support;</li>
          <li>Analyze usage to improve reliability, performance, and product design, using aggregated or de-identified data where practical;</li>
          <li>Send service communications (receipts, security alerts, product updates) and, where permitted, marketing communications you can opt out of at any time;</li>
          <li>Detect, prevent, and respond to fraud, abuse, security incidents, and legal obligations.</li>
        </ul>
        <p class="my-5">
          We do not sell personal data, and we do not use Workspace Content to
          advertise to you or to third parties. We do share limited identifiers
          with advertising partners to measure and improve our own ads, as
          described in section 11.2.
        </p>

        <h2 class="font-bold text-xl my-10">4. AI features and your data</h2>
        <p class="my-5">
          AI features in the Service (for example, AI summaries of calls and
          messages, AI-drafted workflows and content) are provided through
          third-party AI subprocessors acting on our instructions. Workspace
          Content processed by these features is used only to deliver the
          requested output to you. We do not use Workspace Content to train
          general-purpose AI models, and we instruct our AI subprocessors not to
          use it for that purpose. AI output can be inaccurate; you are
          responsible for reviewing it before relying on it.
        </p>

        <h2 class="font-bold text-xl my-10">5. Google API Services User Data Policy</h2>
        <p class="my-5">
          The Service's use and transfer to any other app of information
          received from Google APIs will adhere to the
          <a
            href="https://developers.google.com/terms/api-services-user-data-policy"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[#1ba7ce] underline"
            >Google API Services User Data Policy</a
          >, including the Limited Use requirements. In particular:
        </p>
        <ul class="list-disc pl-6 space-y-3 my-5">
          <li>We use Google user data only to provide and improve the features you connect: Google Calendar synchronization (reading and writing calendar events you select), Google Meet link creation for your scheduled events, and, when enabled, Gmail integration that syncs email into your shared inbox;</li>
          <li>We do not transfer Google user data to third parties except as necessary to provide these features, for security or legal compliance, or with your explicit instruction;</li>
          <li>We do not use Google user data for advertising, and we do not sell it;</li>
          <li>We do not use Google user data, including aggregated, anonymized, or derived data, to develop, improve, or train generalized or foundational AI or machine learning models, and we do not transfer it to AI providers that would use it for that purpose;</li>
          <li>We do not permit humans to read Google user data except with your affirmative consent for a specific purpose (for example, resolving a support issue you asked us to investigate), where necessary for security, or to comply with law;</li>
          <li>You can disconnect any Google integration at any time from within the Service, which stops further data access; you can also revoke access from your Google Account permissions page.</li>
        </ul>

        <h2 class="font-bold text-xl my-10">6. Payments, Stripe, and Stripe Connect</h2>
        <p class="my-5">
          Workspace369 uses Stripe to process subscription payments and to
          provide payment infrastructure. Your payment card details are provided
          directly to Stripe and are governed by the
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[#1ba7ce] underline"
            >Stripe Privacy Policy</a
          >; we never see or store your full card number.
        </p>
        <p class="my-5">
          If you accept payments from your own clients through the Service, you
          do so using a Stripe connected account subject to the Stripe Connected
          Account Agreement and the Stripe Services Agreement. When your clients
          pay you through the Service, we charge an application fee of one
          percent (1%) of each transaction amount, which is deducted from the
          transaction before settlement, in addition to Stripe's processing
          fees. Stripe's processing fees are set by Stripe and described in your
          Stripe agreement. Transaction records, including the application fee,
          are visible in the Service and in your Stripe dashboard. Stripe, not
          Workspace369, is responsible for the transmission and settlement of
          funds to your bank account; Workspace369 is not a money transmitter,
          bank, or payment institution, and we do not hold your funds.
        </p>

        <h2 class="font-bold text-xl my-10">7. How we share information</h2>
        <p class="my-5">We share personal data only in these circumstances:</p>
        <ul class="list-disc pl-6 space-y-3 my-5">
          <li><strong>Subprocessors and service providers</strong> that help us operate the Service under contract, including: cloud hosting and infrastructure providers; Stripe (payments); Google (authentication, and Calendar/Meet/Gmail integrations you enable); Firebase (authentication infrastructure); AI model providers (AI features you use); email and SMS/voice delivery providers; analytics providers on our website; and advertising measurement providers (Google and Meta), which receive browser identifiers and conversion events such as sign-ups, trial starts, and subscription payments (Meta also receives hashed contact details) unless you have opted out. Each is bound to process data only on our instructions and to protect it;</li>
          <li><strong>Within your workspace</strong>: Workspace Content is visible to the members and clients you grant access to, including through client portals and public forms you enable;</li>
          <li><strong>Legal requirements</strong>: where required by law, regulation, legal process, or to protect the rights, safety, and security of Workspace369, our users, or the public;</li>
          <li><strong>Business transfers</strong>: in connection with a merger, acquisition, financing, or sale of assets, subject to this Policy's protections;</li>
          <li><strong>With your direction or consent</strong>.</li>
        </ul>

        <h2 class="font-bold text-xl my-10">8. International data transfers</h2>
        <p class="my-5">
          The Service is operated from the United States, and personal data is
          processed primarily in the United States. If you use the Service from
          the European Economic Area, the United Kingdom, Switzerland, or
          another region with data transfer restrictions, you understand that
          your personal data will be transferred to and processed in the United
          States. Where required, we rely on lawful transfer mechanisms, such as
          the European Commission's Standard Contractual Clauses and the UK
          International Data Transfer Addendum, for transfers of personal data
          protected by those laws.
        </p>

        <h2 class="font-bold text-xl my-10">9. Data retention</h2>
        <p class="my-5">
          We keep account data and Workspace Content for as long as your account
          is active or as needed to provide the Service. When you delete content
          or close your account, we delete or de-identify it from production
          systems within a commercially reasonable period, except where we must
          retain records to comply with legal, tax, accounting, or audit
          obligations (for example, transaction and payout records), resolve
          disputes, or enforce agreements. Encrypted backups roll off according
          to our backup schedule.
        </p>

        <h2 class="font-bold text-xl my-10">10. Security</h2>
        <p class="my-5">
          We use commercially reasonable technical and organizational measures
          designed to protect personal data, including encryption in transit,
          access controls, least-privilege internal access, and monitoring. No
          method of transmission or storage is completely secure; if we become
          aware of a breach of security leading to accidental or unlawful
          access to personal data, we will notify affected users and regulators
          as required by applicable law.
        </p>

        <h2 class="font-bold text-xl my-10">11. Your privacy rights</h2>
        <h3 class="font-semibold text-lg my-4">11.1 EEA, UK, and Switzerland</h3>
        <p class="my-5">
          Where the GDPR or equivalent laws apply, you have the right to access,
          correct, delete, restrict, or object to the processing of your
          personal data, the right to data portability, and the right to lodge a
          complaint with your supervisory authority. Where we process Workspace
          Content as a processor for a customer, please direct your request to
          that customer (the controller); we will assist them in responding.
        </p>
        <h3 class="font-semibold text-lg my-4">11.2 United States (including California)</h3>
        <p class="my-5">
          Residents of California and other US states with comprehensive
          privacy laws have the right to know, access, correct, delete, and
          obtain a portable copy of their personal information, and to opt out
          of the sale or sharing of personal information for cross-context
          behavioral advertising. We do not sell personal information. We do
          share limited personal information for cross-context behavioral
          advertising: when you visit our website, submit a form, or sign up,
          we may send browser identifiers and conversion events (such as a
          sign-up, trial start, or subscription payment) to Meta and Google,
          and hashed contact details to Meta, to measure and optimize our
          advertising.
          You can opt out at any time with the "Your privacy choices" link in
          the website footer, and we treat a Global Privacy Control signal from
          your browser as an opt-out. To opt out of sharing tied to an account
          or a form you already submitted, contact us as described in section
          11.3. We do not use or disclose sensitive personal information for
          purposes requiring a right to limit under the CPRA.
          We will not discriminate against you for exercising your rights.
        </p>
        <h3 class="font-semibold text-lg my-4">11.3 Exercising your rights</h3>
        <p class="my-5">
          You can exercise most rights directly in the Service (profile
          settings, exports, deletion) or by emailing
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >. We will verify your identity before acting and respond within the
          period required by applicable law.
        </p>

        <h2 class="font-bold text-xl my-10">12. Communications involving your clients and contacts</h2>
        <p class="my-5">
          The Service lets you communicate with your clients and contacts by
          email, SMS, and voice, and may record or transcribe calls and
          voicemail. You are responsible for providing any notices and obtaining
          any consents required for those communications and recordings under
          applicable law, including call-recording consent laws and
          messaging-consent laws such as the TCPA. We process your contacts'
          data on your behalf to deliver those features; their data is handled
          as Workspace Content under this Policy.
        </p>

        <h2 class="font-bold text-xl my-10">13. Cookies</h2>
        <p class="my-5">
          We use strictly necessary cookies (authentication, security,
          preferences, and your consent choice), analytics cookies (Google
          Analytics, Microsoft Clarity, Ahrefs), and marketing cookies (Google
          Ads, Meta Pixel, Rewardful, and our own campaign-attribution cookie).
          Visitors in the EU/EEA, the UK, and Switzerland are asked before any
          analytics or marketing cookies load. Elsewhere they load by default,
          and you can switch them off at any time from the "Your privacy
          choices" link in the website footer; a Global Privacy Control signal
          switches marketing cookies off automatically. You can also block
          cookies through your browser settings. Full details, including cookie names, purposes, and
          durations, are in our
          <nuxt-link :to="localePath('/cookie-policy')" class="text-[#1ba7ce] underline"
            >Cookie Policy</nuxt-link
          >.
        </p>

        <h2 class="font-bold text-xl my-10">14. Children's privacy</h2>
        <p class="my-5">
          The Service is a business product and is not directed to children. We
          do not knowingly collect personal data from children under 16. If you
          believe a child has provided us personal data, contact us and we will
          delete it.
        </p>

        <h2 class="font-bold text-xl my-10">15. Do Not Track</h2>
        <p class="my-5">
          There is no industry standard for responding to Do Not Track signals,
          so we do not respond to them at this time. You can use the cookie and
          analytics choices described above instead.
        </p>

        <h2 class="font-bold text-xl my-10">16. Changes to this Policy</h2>
        <p class="my-5">
          We may update this Privacy Policy from time to time. We will post the
          updated version with a new "Last Updated" date and, for material
          changes, provide additional notice through the Service or by email.
          Continued use of the Service after the effective date of an update
          constitutes acceptance of the updated Policy.
        </p>

        <h2 class="font-bold text-xl my-10">17. Contact</h2>
        <p class="my-5">
          Workspace369 LLC, 8225 Emerald Winds Circle, Boynton Beach, FL 33473,
          USA. Email:
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
const { localePath } = useWorkspaceLocale();
useHead({
  title: "Privacy Policy | Workspace369 Data Practices",
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: "description",
      content:
        "Read the Workspace369 privacy policy to understand how Workspace369 collects, uses, stores, shares, and protects personal data across the website and app.",
    },
    { name: "twitter:title", content: "Privacy Policy | Workspace369 Data Practices" },
    {
      name: "twitter:description",
      content:
        "Read how Workspace369 collects, uses, stores, shares, and protects personal data across the website and app.",
    },
    { name: "twitter:card", content: "summary_large_image" },
    { property: "og:title", content: "Privacy Policy | Workspace369 Data Practices" },
    {
      property: "og:description",
      content:
        "Read how Workspace369 collects, uses, stores, shares, and protects personal data across the website and app.",
    },
  ],
});
</script>
